import React from "react";
import {
  Drawer, Box, Typography, IconButton, Button, CircularProgress, Alert, Stack, Chip,
  TextField
} from "@mui/material";
import { Close, CreditCardRounded, LockRounded, OpenInNewRounded } from "@mui/icons-material";

import {
  brandThemeCssVars,
  DEFAULT_BRAND_THEME,
  normalizeBrandTheme,
} from "../themeTokens";
import { toSafePaymentRedirectUrl } from "../paymentUrl";

export default function PaymentSheet({
  open,
  payUrl,
  message,
  checking,
  iframeError,
  paymentProvider = "PayU",
  brandName,
  registrationFailed = false,
  registering = false,
  clientSessionRequired = false,
  onOpenPayU,
  onManualCheck,
  onCancelOrder,
  onRetryRegistration,
  onClientLogin,
  onClose,
  t = (key, fallback) => fallback || key,
  brandTheme = DEFAULT_BRAND_THEME,
}) {
  const paidBtnRef = React.useRef(null);
  const loginInputRef = React.useRef(null);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loginError, setLoginError] = React.useState("");
  const [loginPending, setLoginPending] = React.useState(false);
  const c = normalizeBrandTheme(brandTheme);
  const paymentOwner = String(brandName || "").trim() || "Restaurant";
  const safePayUrl = toSafePaymentRedirectUrl(payUrl);

  React.useEffect(() => {
    if (open) {
      const initialTarget = clientSessionRequired
        ? loginInputRef.current
        : paidBtnRef.current;
      initialTarget?.focus();
    }
  }, [clientSessionRequired, open]);

  React.useEffect(() => {
    if (!open) {
      setUsername("");
      setPassword("");
      setLoginError("");
      setLoginPending(false);
    }
  }, [open]);

  const submitClientLogin = async (event) => {
    event.preventDefault();
    if (loginPending || !onClientLogin) return;
    setLoginPending(true);
    setLoginError("");
    try {
      const result = await onClientLogin({ username, password });
      if (!result?.ok) {
        setLoginError(t("payment.clientLoginFailed"));
        return;
      }
      setUsername("");
      setPassword("");
    } finally {
      setLoginPending(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose || (() => {})}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "payment-sheet-title",
        sx: {
          ...brandThemeCssVars(c),
          height: { xs: "96dvh", sm: "min(780px, 88dvh)" },
          minHeight: { sm: 620 },
          maxHeight: { xs: "96dvh", sm: "88dvh" },
          width: { xs: "100%", sm: 620, md: 720 },
          maxWidth: "calc(100vw - 32px)",
          mx: { xs: 0, sm: "auto" },
          mb: { xs: 0, sm: 3 },
          borderTopLeftRadius: { xs: 24, sm: 28 },
          borderTopRightRadius: { xs: 24, sm: 28 },
          borderBottomLeftRadius: { xs: 0, sm: 28 },
          borderBottomRightRadius: { xs: 0, sm: 28 },
          overflow: "hidden",
          bgcolor: c.background,
          boxShadow: "0 28px 84px rgba(33,20,12,0.24)",
          "@media (prefers-reduced-motion: reduce)": {
            "&, & *": {
              animationDuration: "0.01ms !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        {/* Header */}
        <Box
          component="header"
          sx={{
            position: "relative",
            overflow: "hidden",
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            borderBottom: "1px solid rgba(124,45,18,0.10)",
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--brand-bg) 96%, white) 0%, color-mix(in srgb, var(--brand-primary) 11%, var(--brand-bg)) 58%, color-mix(in srgb, var(--brand-cta) 10%, var(--brand-bg)) 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(160deg, color-mix(in srgb, var(--brand-primary) 13%, transparent) 0%, transparent 42%, color-mix(in srgb, var(--brand-cta) 9%, transparent) 100%)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: c.primary,
                color: c.onPrimary,
                border: "1px solid rgba(124,45,18,0.10)",
                boxShadow: "0 14px 34px rgba(33,20,12,0.12)",
                flexShrink: 0,
              }}
            >
              <LockRounded />
            </Box>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Chip
                size="small"
                icon={<CreditCardRounded />}
                label={paymentProvider}
                sx={{
                  height: 24,
                  mb: 0.75,
                  color: c.text,
                  bgcolor: "rgba(255,255,255,0.68)",
                  border: "1px solid rgba(124,45,18,0.12)",
                  fontWeight: 900,
                  "& .MuiChip-icon": {
                    color: c.accent,
                  },
                }}
              />

              <Typography
                id="payment-sheet-title"
                variant="h6"
                sx={{
                  fontWeight: 950,
                  lineHeight: 1.15,
                  color: c.text,
                  letterSpacing: 0,
                }}
              >
                {t("payment.finalize")}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.3, lineHeight: 1.35 }}
              >
                {t("payment.confirmedAfter")}
              </Typography>
            </Box>

            {onClose ? (
              <IconButton
                onClick={onClose}
                size="small"
                aria-label={t("payment.close")}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.25,
                  color: c.text,
                  bgcolor: "rgba(255,255,255,0.64)",
                  border: "1px solid rgba(124,45,18,0.12)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                  "&:focus-visible": {
                    outline: `3px solid ${c.cta}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          {clientSessionRequired ? (
            <Box
              component="form"
              onSubmit={submitClientLogin}
              aria-busy={loginPending}
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.82)",
                border: "1px solid rgba(211,47,47,0.22)",
              }}
            >
              <Stack spacing={1.25}>
                <Alert severity="warning">
                  {t("payment.clientSessionRequired")}
                </Alert>
                {loginError ? (
                  <Alert id="payment-client-login-error" severity="error">
                    {loginError}
                  </Alert>
                ) : null}
                <TextField
                  label={t("payment.clientUsername")}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  inputRef={loginInputRef}
                  size="small"
                  required
                  disabled={loginPending}
                  aria-describedby={
                    loginError ? "payment-client-login-error" : undefined
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 48,
                      borderRadius: 2.25,
                    },
                  }}
                />
                <TextField
                  label={t("payment.clientPassword")}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  size="small"
                  required
                  disabled={loginPending}
                  aria-describedby={
                    loginError ? "payment-client-login-error" : undefined
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 48,
                      borderRadius: 2.25,
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    loginPending || !username.trim() || !password
                  }
                  sx={{
                    minHeight: 48,
                    borderRadius: 2.25,
                    fontWeight: 900,
                    "&:focus-visible": {
                      outline: `3px solid ${c.cta}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {loginPending
                    ? t("payment.clientSigningIn")
                    : t("payment.clientSignIn")}
                </Button>
              </Stack>
            </Box>
          ) : null}

          {message ? (
            <Alert
              role="status"
              aria-live="polite"
              severity="info"
              variant="outlined"
              sx={{
                mb: 1.5,
                borderRadius: 2.25,
                bgcolor: "rgba(255,255,255,0.76)",
                borderColor: "rgba(15,118,110,0.20)",
              }}
            >
              {message}
            </Alert>
          ) : null}

          <Stack
            role="list"
            aria-label={t("payment.finalize")}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            {[t("payment.stepPay"), t("payment.stepReturn")].map((text, index) => (
              <Box
                key={text}
                role="listitem"
                sx={{
                  flex: 1,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  p: 1.25,
                  borderRadius: 2.25,
                  bgcolor: "rgba(255,255,255,0.70)",
                  border: "1px solid rgba(124,45,18,0.10)",
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: index === 0 ? c.primary : "rgba(15,118,110,0.12)",
                    color: index === 0 ? c.onPrimary : c.cta,
                    fontSize: 12,
                    fontWeight: 950,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: c.text,
                    fontWeight: 800,
                    lineHeight: 1.35,
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              minHeight: { xs: 330, sm: iframeError ? "auto" : 420 },
              borderRadius: 2.75,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "rgba(124,45,18,0.12)",
              bgcolor: "rgba(255,255,255,0.86)",
              boxShadow: "0 16px 42px rgba(33,20,12,0.10)",
            }}
          >
            {registrationFailed ? (
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 2,
                  minHeight: { xs: 300, sm: 280 },
                }}
              >
                <Alert severity="warning">
                  {clientSessionRequired
                    ? t("payment.clientSessionRequired")
                    : t("payment.registrationFailed")}
                </Alert>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={onRetryRegistration}
                  disabled={
                    registering ||
                    clientSessionRequired ||
                    !onRetryRegistration
                  }
                  sx={{
                    minHeight: 48,
                    borderRadius: 2.25,
                    fontWeight: 900,
                    "&:focus-visible": {
                      outline: `3px solid ${c.cta}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {registering
                    ? t("payment.retryingRegistration")
                    : t("payment.retryRegistration")}
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("payment.sameOrderRetry")}
                </Typography>
              </Box>
            ) : !iframeError && safePayUrl ? (
              <iframe
                title={t("payment.online")}
                src={safePayUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 420,
                  border: 0,
                  display: "block",
                  background: c.surface,
                }}
                sandbox="allow-forms allow-scripts allow-same-origin allow-popups"
              />
            ) : (
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 2,
                  minHeight: { xs: 300, sm: 280 },
                }}
              >
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    borderRadius: 2.25,
                    bgcolor: "rgba(255,250,243,0.76)",
                    borderColor: "rgba(15,118,110,0.20)",
                  }}
                >
                  {t("payment.secure")
                    .replaceAll("{provider}", paymentProvider)
                    .replace("{owner}", paymentOwner)}
                </Alert>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={onOpenPayU}
                  disabled={!safePayUrl}
                  endIcon={<OpenInNewRounded />}
                  sx={{
                    minHeight: 50,
                    borderRadius: 2.25,
                    fontWeight: 950,
                    py: 1.2,
                    boxShadow: safePayUrl
                      ? "0 12px 28px rgba(247,181,0,0.20)"
                      : "none",
                    "&:focus-visible": {
                      outline: `3px solid ${c.cta}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {t("payment.openOnline")}
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                    lineHeight: 1.5,
                  }}
                >
                  {t("payment.cardOnlyProvider").replaceAll("{provider}", paymentProvider)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer actions */}
        <Box
          component="footer"
          sx={{
            p: 2,
            pb: "max(16px, env(safe-area-inset-bottom))",
            bgcolor:
              "color-mix(in srgb, var(--brand-bg, #fffaf3) 96%, transparent)",
            backdropFilter: "blur(16px)",
            borderTop: "1px solid rgba(124,45,18,0.10)",
            boxShadow: "0 -12px 32px rgba(33,20,12,0.10)",
          }}
        >
          <Box
            role="status"
            aria-live="polite"
            sx={{ display: "flex", alignItems: "center", mb: 1.25 }}
          >
            {checking ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t("payment.checking")}
                </Typography>
              </>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {t("payment.afterPay")}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onManualCheck}
              ref={paidBtnRef}
              size="large"
              disabled={
                checking ||
                registering ||
                registrationFailed ||
                clientSessionRequired ||
                !safePayUrl
              }
              sx={{
                flexGrow: 1,
                minHeight: 50,
                borderRadius: 2.25,
                fontWeight: 950,
                py: 1.1,
                boxShadow: !checking
                  ? "0 12px 28px rgba(247,181,0,0.20)"
                  : "none",
                "&:focus-visible": {
                  outline: `3px solid ${c.cta}`,
                  outlineOffset: 2,
                },
              }}
            >
              {checking ? t("payment.checkingShort") : t("payment.iPaid")}
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={onCancelOrder}
              disabled={checking || registering || clientSessionRequired}
              sx={{
                minHeight: 48,
                borderRadius: 2.25,
                fontWeight: 900,
                px: 2,
                whiteSpace: "nowrap",
                "&:focus-visible": {
                  outline: `3px solid ${c.cta}`,
                  outlineOffset: 2,
                },
              }}
            >
              {t("payment.cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
